/// <reference types="google.maps" />
// Session 63 §1.7 — admin AddressAutocomplete.
//
// Loads Google Places via @googlemaps/js-api-loader (browser CORS-compliant
// path; mobile uses direct REST because React Native has no CORS constraints).
// Emits the same 9-field AddressResult contract as mobile so admin writes
// structured geocoded data to businesses / clients / locations / work_orders.
//
// Session tokens are implemented from day one: fresh UUID on mount, reused
// across every keystroke through the terminating details call, then rotated.
// Google bills the autocomplete + details calls as a single session.

import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { palette } from '../../styles/tokens';
import { log } from '../../lib/logger';

setOptions({
  key: import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string,
  v: 'weekly',
  libraries: ['places'],
});

export interface AddressResult {
  address: string;      // "123 Main St"
  city: string;         // locality, fallback sublocality, fallback postal_town
  province: string;     // administrative_area_level_1 SHORT name (e.g. "ON")
  postalCode: string;
  country: string;      // country SHORT name ("CA")
  latitude: number;
  longitude: number;
  placeId: string;
  fullAddress: string;  // Google's formatted_address
}

export interface AddressAutocompleteProps {
  onSelect: (result: AddressResult) => void;
  placeholder?: string;
  initialValue?: string;
  types?: 'address' | 'geocode|establishment';
  disabled?: boolean;
}

const ROW: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '9px 12px', borderRadius: 8,
  border: `1px solid ${palette.g200}`,
  background: palette.white,
  boxSizing: 'border-box' as const,
};

const INPUT: React.CSSProperties = {
  flex: 1,
  border: 'none', outline: 'none',
  fontFamily: 'inherit', fontSize: 13.5, color: palette.g800,
  background: 'transparent',
};

const DROPDOWN: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
  background: palette.white,
  border: `1px solid ${palette.g200}`,
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  maxHeight: 300, overflowY: 'auto',
  zIndex: 50,
};

const PREDICTION_ROW: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '12px 14px', minHeight: 48,
  cursor: 'pointer',
  borderBottom: `1px solid ${palette.g100}`,
  fontFamily: 'inherit',
  background: 'transparent', border: 'none', width: '100%',
  textAlign: 'left' as const,
};

const PRED_MAIN: React.CSSProperties = {
  fontSize: 13.5, color: palette.g800,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const PRED_SECONDARY: React.CSSProperties = {
  fontSize: 12, color: palette.g500, marginTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const EMPTY_STATE: React.CSSProperties = {
  padding: '14px 14px', fontSize: 12.5, color: palette.g500,
};

export function AddressAutocomplete({
  onSelect, placeholder, initialValue, types = 'address', disabled,
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(initialValue ?? '');
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [ready, setReady] = useState(false);

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    importLibrary('places')
      .then((placesLib) => {
        if (cancelled) return;
        autocompleteServiceRef.current = new placesLib.AutocompleteService();
        const attribution = document.createElement('div');
        placesServiceRef.current = new placesLib.PlacesService(attribution);
        sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
        setReady(true);
      })
      .catch((err: unknown) => {
        log.error('[AddressAutocomplete] loader failed:', err);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!ready || query.length < 3 || !autocompleteServiceRef.current) {
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      setLoading(true);
      const typeList = types === 'geocode|establishment' ? ['geocode', 'establishment'] : ['address'];
      void autocompleteServiceRef.current!.getPlacePredictions(
        {
          input: query,
          componentRestrictions: { country: 'ca' },
          types: typeList,
          sessionToken: sessionTokenRef.current!,
        },
        (results: google.maps.places.AutocompletePrediction[] | null) => {
          setLoading(false);
          setPredictions(results ?? []);
          setShowDropdown(true);
        },
      );
    }, 300);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query, types, ready]);

  // Derived: stale predictions stay in state but aren't rendered when query
  // drops below 3 chars — avoids a setState-in-effect cascading render.
  const predsToShow = query.length >= 3 ? predictions : [];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (prediction: google.maps.places.AutocompletePrediction) => {
    if (!placesServiceRef.current) return;
    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
        sessionToken: sessionTokenRef.current!,
      },
      (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
          log.error('[AddressAutocomplete] details failed:', status);
          return;
        }
        const result = parseAddressComponents(place);
        onSelect(result);
        setQuery(result.fullAddress);
        setShowDropdown(false);
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      },
    );
  };

  const showEmpty = showDropdown && !loading && query.length >= 3 && predsToShow.length === 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
      <div style={ROW}>
        <MapPin size={14} color={palette.g400} />
        <input
          type="text"
          style={INPUT}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
          onFocus={() => { if (predsToShow.length > 0) setShowDropdown(true); }}
          placeholder={placeholder ?? 'Start typing an address'}
          autoComplete="off"
          spellCheck={false}
        />
        {loading && <span style={{ fontSize: 12, color: palette.g400 }}>Searching…</span>}
      </div>

      {showDropdown && predsToShow.length > 0 && (
        <div style={DROPDOWN}>
          {predsToShow.map((pred) => (
            <button
              key={pred.place_id}
              type="button"
              style={PREDICTION_ROW}
              onMouseEnter={(e) => (e.currentTarget.style.background = palette.g50)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={() => handleSelect(pred)}
            >
              <MapPin size={14} color={palette.g400} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={PRED_MAIN}>
                  {pred.structured_formatting?.main_text ?? pred.description}
                </div>
                <div style={PRED_SECONDARY}>
                  {pred.structured_formatting?.secondary_text ?? ''}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showEmpty && (
        <div style={DROPDOWN}>
          <div style={EMPTY_STATE}>No matches found</div>
        </div>
      )}
    </div>
  );
}

function parseAddressComponents(place: google.maps.places.PlaceResult): AddressResult {
  const comp = (type: string, short = false): string => {
    const c = place.address_components?.find((c) => c.types.includes(type));
    return short ? (c?.short_name ?? '') : (c?.long_name ?? '');
  };

  const address = [comp('street_number'), comp('route')].filter(Boolean).join(' ').trim();

  return {
    address,
    city: comp('locality') || comp('sublocality') || comp('postal_town'),
    province: comp('administrative_area_level_1', true),
    postalCode: comp('postal_code'),
    country: comp('country', true),
    latitude: place.geometry?.location?.lat() ?? 0,
    longitude: place.geometry?.location?.lng() ?? 0,
    placeId: place.place_id ?? '',
    fullAddress: place.formatted_address ?? '',
  };
}
